import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import Drawer from '@mui/material/Drawer';
import { Button, IconButton, Radio } from '@mui/material';
import { CloseOutlined, ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import Currencies from 'arbitrip-common/general/utils/Currencies';
import { RTL } from 'arbitrip-common/translation/const';

import CashWalletConstants, { deriveNetVat, isPartialAmountInput } from './CashWalletConstants';
import { formatMoney } from './CashWalletMoney';
import InvoiceDropzone from './InvoiceDropzone.react';
import ExitConfirmationDialog from './ExitConfirmationDialog.react';

const {
    TAX_CLASSIFICATION,
    STATUS,
    TAX_INLINE_CODES,
    AMOUNT_INLINE_CODES,
    INVOICE_INLINE_CODES,
    AMOUNT_RE,
    DATE_FORMAT,
    DEFAULT_CURRENCY,
} = CashWalletConstants;

// Stands in for a money figure the invoice card cannot state yet, because the typed amount is not in the
// exact submittable format (e.g. the intermediate '100.').
const AMOUNT_PLACEHOLDER = '-';

const deriveBreakdown = (amount, taxClassification) => {
    const amountTrimmed = String(amount).trim();
    const amountNumber = Number(amountTrimmed);
    // Deliberately looser than AMOUNT_RE: this only gates the LIVE PREVIEW (VAT / payout rows, invoice
    // breakdown, Bill To), so an intermediate typing state like '100.' — which `Number` parses fine but
    // AMOUNT_RE rejects — must not blink the whole card away. Submission stays on the strict pattern.
    const amountValid = amountTrimmed !== '' && Number.isFinite(amountNumber) && amountNumber > 0;
    // The strict counterpart: the amount is in the exact submittable format. Consumers that must not
    // state a figure the agent cannot actually submit (the invoice card) key off this, not `active`.
    const formatValid = AMOUNT_RE.test(amountTrimmed) && amountNumber > 0;
    const isExempt = taxClassification === TAX_CLASSIFICATION.EXEMPT;
    const isAuthorised = taxClassification === TAX_CLASSIFICATION.AUTHORISED;
    // The entered figure is the NET amount; `payout` is what the agent actually receives (gross for
    // Authorised, net-only for Exempt) — never the raw entered value.
    const { net, vat, amount: payout } = deriveNetVat(amountNumber, taxClassification);

    return {
        active: amountValid && (isExempt || isAuthorised),
        formatValid,
        isExempt,
        net,
        vat,
        payout,
    };
};

const TaxClassificationSelect = ({ t, value, error, onChange }) => {
    const options = [
        { key: TAX_CLASSIFICATION.NONE, label: t('Drawer.TaxNone'), hebrew: null, hint: null },
        {
            key: TAX_CLASSIFICATION.AUTHORISED,
            label: t('Drawer.TaxAuthorised'),
            hebrew: 'מורשה',
            hint: t('Drawer.TaxAuthorisedHint'),
        },
        {
            key: TAX_CLASSIFICATION.EXEMPT,
            label: t('Drawer.TaxExempt'),
            hebrew: 'פטור',
            hint: t('Drawer.TaxExemptHint'),
        },
    ];

    return (
        <div className="cash-wallet-drawer-section cash-wallet-drawer-card">
            <div className="cash-wallet-drawer-section-title">{t('Drawer.TaxSectionTitle')}</div>
            {options.map((option) => (
                <label key={option.key} className="cash-wallet-tax-option">
                    <Radio
                        color="primary"
                        size="small"
                        checked={value === option.key}
                        onChange={() => onChange(option.key)}
                        value={option.key}
                        sx={{ padding: 0 }}
                    />
                    <span className="cash-wallet-tax-label">
                        {option.hebrew ? `${option.label} | ${option.hebrew}` : option.label}
                    </span>
                    {option.hint && <span className="cash-wallet-tax-hint">{option.hint}</span>}
                </label>
            ))}
            {error && <div className="cash-wallet-drawer-error">{error}</div>}
        </div>
    );
};

const AmountField = ({
    t,
    amount,
    onChange,
    netBalance,
    currency,
    taxClassification,
    expectedPayout,
    validationError,
    backendError,
}) => {
    const payoutDate = expectedPayout ? moment(expectedPayout).format(DATE_FORMAT) : '';
    // Already NET and already capped to what the submit handler accepts — never re-derived here.
    const balanceDisplay = formatMoney(netBalance, currency);
    const error = validationError || backendError;
    const disabled = taxClassification === TAX_CLASSIFICATION.NONE;

    const { active, isExempt, vat, payout } = deriveBreakdown(amount, taxClassification);

    return (
        <div className="cash-wallet-drawer-section cash-wallet-drawer-card">
            <div className="cash-wallet-drawer-section-title">{t('Drawer.AmountSectionTitle')}</div>
            <div className="cash-wallet-amount-sub">
                <span>{t('Drawer.AmountSubPrefix')}</span>
                <span className="cash-wallet-amount-sub-highlight">{t('Drawer.AmountSubToday')}</span>
                <span>{t('Drawer.AmountSubMiddle')}</span>
                <span className="cash-wallet-amount-sub-highlight">{payoutDate}</span>
            </div>
            <div className="cash-wallet-amount-row cash-wallet-amount-row-strong">
                <span>{t('Balance.AvailableBalance')}</span>
                <span>{balanceDisplay}</span>
            </div>
            <div className="cash-wallet-amount-input-group">
                <label
                    className={`cash-wallet-amount-label${disabled ? ' cash-wallet-amount-label-disabled' : ''}`}
                    htmlFor="cash-wallet-amount-input"
                >
                    {t('Drawer.AmountLabel')}
                </label>
                <input
                    id="cash-wallet-amount-input"
                    type="text"
                    inputMode="decimal"
                    className={`cash-wallet-amount-input${error ? ' cash-wallet-amount-input-error' : ''}`}
                    placeholder={t('Drawer.AmountPlaceholder')}
                    disabled={disabled}
                    value={amount}
                    onChange={(event) => {
                        const next = event.target.value;
                        // Vetoes the keystroke by leaving `value` untouched — a third decimal never lands.
                        if (isPartialAmountInput(next)) {
                            onChange(next);
                        }
                    }}
                />
                {error && <div className="cash-wallet-drawer-error">{error}</div>}
            </div>
            {active && (
                <React.Fragment>
                    <div className="cash-wallet-amount-row">
                        <span>{t('Drawer.Vat')}</span>
                        <span className={isExempt ? 'cash-wallet-amount-row-muted' : undefined}>
                            {isExempt ? t('Drawer.NoVat') : formatMoney(vat, currency)}
                        </span>
                    </div>
                    <div className="cash-wallet-amount-row">
                        <span>{t('Drawer.YouWillReceive')}</span>
                        <span>{formatMoney(payout, currency)}</span>
                    </div>
                </React.Fragment>
            )}
        </div>
    );
};

const BreakdownColumn = ({ label, value, total }) => (
    <div className={`cash-wallet-breakdown-column${total ? ' cash-wallet-breakdown-column-total' : ''}`}>
        <span className="cash-wallet-breakdown-label">{label}</span>
        <span className="cash-wallet-breakdown-value">{value}</span>
    </div>
);

const InvoiceBreakdownCard = ({ t, amount, taxClassification, currency, billTo, onToast }) => {
    const { active, formatValid, isExempt, net, vat, payout } = deriveBreakdown(amount, taxClassification);

    if (!active) {
        return null;
    }

    // The card stays mounted for a loosely-valid amount, but an amount that cannot be submitted must not
    // be presented as an invoice figure — it is dashed out until the format is complete.
    let netDisplay = AMOUNT_PLACEHOLDER;
    let vatDisplay = AMOUNT_PLACEHOLDER;
    let totalDisplay = AMOUNT_PLACEHOLDER;

    if (formatValid) {
        netDisplay = formatMoney(net, currency);
        vatDisplay = formatMoney(vat, currency);
        totalDisplay = formatMoney(payout, currency);
    }

    const copyBreakdown = () => {
        if (!formatValid) {
            return;
        }
        const breakdownLines = isExempt
            ? []
            : [`${t('Drawer.NetAmount')}: ${netDisplay}`, `${t('Drawer.Vat')}: ${vatDisplay}`];
        const lines = breakdownLines.concat(`${t('Drawer.Total')}: ${totalDisplay}`).join('\n');
        if (navigator.clipboard) {
            navigator.clipboard.writeText(lines);
        }
        onToast(t('Drawer.Copied'));
    };

    const copyBillTo = () => {
        if (!billTo) {
            return;
        }
        const lines = [billTo.name, billTo.phone, billTo.email, billTo.address].filter(Boolean).join('\n');
        if (navigator.clipboard) {
            navigator.clipboard.writeText(lines);
        }
        onToast(t('Drawer.Copied'));
    };

    return (
        <div className="cash-wallet-breakdown-card">
            <span className="cash-wallet-breakdown-title">{t('Drawer.InvoiceCardTitle')}</span>
            {active && (
                <div className="cash-wallet-breakdown-columns">
                    {!isExempt && (
                        <React.Fragment>
                            <BreakdownColumn label={t('Drawer.NetAmount')} value={netDisplay} />
                            <BreakdownColumn label={t('Drawer.Vat')} value={vatDisplay} />
                        </React.Fragment>
                    )}
                    <BreakdownColumn label={t('Drawer.Total')} value={totalDisplay} total />
                    <IconButton size="small" aria-label="copy" onClick={copyBreakdown} disabled={!formatValid}>
                        <ContentCopy fontSize="small" />
                    </IconButton>
                </div>
            )}

            {billTo && (
                <div className="cash-wallet-breakdown-billto">
                    <div className="cash-wallet-breakdown-head">
                        <span className="cash-wallet-breakdown-title">{t('Drawer.BillTo')}</span>
                        <IconButton size="small" aria-label="copy" onClick={copyBillTo}>
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </div>
                    <div>{billTo.name}</div>
                    <div>{billTo.phone}</div>
                    <div>{billTo.email}</div>
                    <div>{billTo.address}</div>
                </div>
            )}
        </div>
    );
};

const WithdrawalDrawerFooter = ({ t, canSubmit, busy, onSubmit, onCancel }) => (
    <div className="cash-wallet-drawer-footer">
        <Button
            className="cash-wallet-drawer-submit"
            variant="contained"
            color="primary"
            disabled={!canSubmit || busy}
            onClick={onSubmit}
        >
            {t('Drawer.SubmitRequest')}
        </Button>
        <Button variant="outlined" color="danger" disabled={busy} onClick={onCancel}>
            {t('Drawer.Cancel')}
        </Button>
    </div>
);

const WithdrawalDrawer = ({
    open,
    onClose,
    netBalance,
    currency,
    threshold,
    maxAmount,
    expectedPayout,
    billTo,
    submitStatus,
    submitError,
    onSubmit,
    onToast,
}) => {
    const { t, i18n } = useTranslation('CashWallet');
    const isRTL = i18n.dir() === RTL;
    const resolvedCurrency = currency || DEFAULT_CURRENCY;

    const [taxClassification, setTaxClassification] = useState(TAX_CLASSIFICATION.NONE);
    const [amount, setAmount] = useState('');
    const [invoiceFile, setInvoiceFile] = useState(null);
    const [invoiceError, setInvoiceError] = useState(null);
    const [taxError, setTaxError] = useState(null);
    const [amountBackendError, setAmountBackendError] = useState(null);
    const [exitOpen, setExitOpen] = useState(false);

    const submitStatusRef = useRef(STATUS.INITIAL);

    useEffect(() => {
        if (open) {
            setTaxClassification(TAX_CLASSIFICATION.NONE);
            setAmount('');
            setInvoiceFile(null);
            setInvoiceError(null);
            setTaxError(null);
            setAmountBackendError(null);
            setExitOpen(false);
            submitStatusRef.current = STATUS.INITIAL;
        }
    }, [open]);

    useEffect(() => {
        if (submitStatusRef.current === submitStatus) {
            return;
        }
        submitStatusRef.current = submitStatus;

        if (submitStatus !== STATUS.ERROR) {
            return;
        }
        if (TAX_INLINE_CODES.includes(submitError)) {
            setTaxError(t(`SubmitError.${submitError}`));
        } else if (AMOUNT_INLINE_CODES.includes(submitError)) {
            setAmountBackendError(t(`SubmitError.${submitError}`));
        } else if (INVOICE_INLINE_CODES.includes(submitError)) {
            setInvoiceError(t(`SubmitError.${submitError}`));
        }
    }, [submitStatus, submitError]);

    const amountTrimmed = String(amount).trim();
    const amountFormatOk = AMOUNT_RE.test(amountTrimmed);
    const amountNumber = Number(amountTrimmed);

    // The ceiling is compared NET-vs-NET against the SERVER's `net_balance`, never a locally re-derived
    // one: the server's figure is the inverse of the debit the ledger will post, so a ceiling derived
    // from the gross balance can be a cent too generous and let through a request that strands In
    // Review. Mirrors packages/cash-wallet/submit-handler.js step 5.
    // `deriveNetVat` derives a net for any classification, so gate here to keep this check dormant until
    // the agent picks one.
    const hasTaxClassification =
        taxClassification === TAX_CLASSIFICATION.EXEMPT || taxClassification === TAX_CLASSIFICATION.AUTHORISED;
    const enteredNet = hasTaxClassification ? deriveNetVat(amountNumber, taxClassification).net : 0;
    // The maximum caps the PAYOUT, not the entered net — an Authorised agent is paid 18% above what they
    // type, so capping the input would let them past it. This is the same figure the cards render as
    // Total / You'll receive, so it is read off the preview's own breakdown rather than derived a second
    // time. Gated on the classification for the same reason as `enteredNet`, and a stronger one: the
    // payout BASIS is the classification (gross vs net), so there is no payout to cap until one is picked.
    const enteredPayout = hasTaxClassification ? deriveBreakdown(amount, taxClassification).payout : 0;

    let amountValidationError = null;
    if (amountTrimmed !== '' && amountFormatOk) {
        if (enteredNet > netBalance) {
            amountValidationError = t('Drawer.ValidationNoBalance');
        } else if (amountNumber < threshold) {
            amountValidationError = t('Drawer.ValidationMinAmount', {
                threshold: Currencies.getPriceWithCurrency(threshold, resolvedCurrency),
            });
        } else if (enteredPayout > maxAmount) {
            amountValidationError = t('Drawer.ValidationMaxAmount', {
                limit: Currencies.getPriceWithCurrency(maxAmount, resolvedCurrency),
            });
        }
    }

    const amountGateOk =
        amountFormatOk &&
        amountNumber > 0 &&
        amountNumber >= threshold &&
        enteredNet <= netBalance &&
        enteredPayout <= maxAmount;
    const canSubmit =
        taxClassification !== TAX_CLASSIFICATION.NONE && amountGateOk && Boolean(invoiceFile) && !invoiceError;

    const busy = submitStatus === STATUS.BUSY;

    const isDirty =
        taxClassification !== TAX_CLASSIFICATION.NONE || amountTrimmed !== '' || Boolean(invoiceFile);

    const requestClose = () => {
        if (isDirty) {
            setExitOpen(true);
        } else {
            onClose();
        }
    };

    const handleAmountChange = (value) => {
        setAmount(value);
        setAmountBackendError(null);
    };

    const handleTaxChange = (value) => {
        setTaxClassification(value);
        setTaxError(null);
    };

    const handleInvoiceSelect = (file, error) => {
        setInvoiceFile(file);
        setInvoiceError(error);
    };

    const handleSubmit = () => {
        onSubmit({
            amount: amountTrimmed,
            tax_classification: taxClassification,
            invoice: invoiceFile,
        });
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={requestClose}
            className="cash-wallet-withdrawal-drawer"
            variant="temporary"
            SlideProps={{ appear: true }}
            sx={{
                '& .MuiDrawer-paper': {
                    direction: isRTL ? 'rtl' : 'ltr',
                },
            }}
        >
            <div className="cash-wallet-drawer-inner">
                <div className="cash-wallet-drawer-header">
                    <span className="cash-wallet-drawer-title">{t('Drawer.Title')}</span>
                    <IconButton className="cash-wallet-drawer-close" aria-label="close" onClick={requestClose}>
                        <CloseOutlined />
                    </IconButton>
                </div>

                <div className="cash-wallet-drawer-body">
                    <TaxClassificationSelect t={t} value={taxClassification} error={taxError} onChange={handleTaxChange} />
                    <AmountField
                        t={t}
                        amount={amount}
                        onChange={handleAmountChange}
                        netBalance={netBalance}
                        currency={resolvedCurrency}
                        taxClassification={taxClassification}
                        expectedPayout={expectedPayout}
                        validationError={amountValidationError}
                        backendError={amountBackendError}
                    />
                    <InvoiceBreakdownCard
                        t={t}
                        amount={amount}
                        taxClassification={taxClassification}
                        currency={resolvedCurrency}
                        billTo={billTo}
                        onToast={onToast}
                    />
                    <div className="cash-wallet-drawer-section">
                        <div className="cash-wallet-drawer-section-title">{t('Drawer.UploadTitle')}</div>
                        <InvoiceDropzone file={invoiceFile} error={invoiceError} onSelect={handleInvoiceSelect} />
                    </div>
                </div>

                <WithdrawalDrawerFooter
                    t={t}
                    canSubmit={canSubmit}
                    busy={busy}
                    onSubmit={handleSubmit}
                    onCancel={requestClose}
                />
            </div>

            <ExitConfirmationDialog
                open={exitOpen}
                onStay={() => setExitOpen(false)}
                onExit={() => {
                    setExitOpen(false);
                    onClose();
                }}
            />
        </Drawer>
    );
};

export default WithdrawalDrawer;
